import { OperationContext } from "@urql/core";
import {
  useMutation as urqlUseMutation,
  UseMutationResponse,
  useQuery as urqlUseQuery,
  UseQueryArgs,
  UseQueryState,
  useSubscription as urqlUseSubscription,
  UseSubscriptionResponse
} from "urql";
import { DocumentNode } from "graphql";
import { OperationResult } from "urql/dist/types";
import {
  SubscriptionHandler,
  UseSubscriptionArgs
} from "urql/dist/types/hooks/useSubscription";
import { datadogLogs } from "@datadog/browser-logs";
import { useEffect } from "react";

type HookReturn<T, V> = UseQueryState<T, V> & {
  executor: (opts?: Partial<OperationContext>) => void;
};

export interface UseQuery<v> extends UseQueryArgs<v> {
  pollInterval?: number;
}

type HookType = <T = any, V = object>(request: UseQuery<V>) => HookReturn<T, V>;

/**
 * Same as the urql useQuery, but add pollInterval back in as on option on the hook.
 * @param pollInterval
 * @param request
 */
export const useQuery: HookType = <T = any, V = object>({
  pollInterval,
  ...request
}: UseQuery<V>): HookReturn<T, V> => {
  // Just pass through request to the urql hook, but if we need values from it like
  // "pause", should extract them separately.
  const { pause } = request;
  const [result, executeQuery] = urqlUseQuery<T, V>({
    ...request,
    pause
  });
  const { fetching } = result;
  useEffect(() => {
    if (!fetching && pollInterval && !pause) {
      // setTimeout works instead of setInterval because it will only fire every time after a fetch completes,
      // So when fetching completes and the bool goes false, this will setTimeout, and when it is up trigger the
      // refetch.  While it refetches another timeout will not be triggered.  When that completes, we will set a new timeout
      // an continue this loop until the hook is unmounted.
      // setInterval should not be used, it would cause reExections to overlap if the fetch took longer than the pollInterval.
      const id = setTimeout(
        () => executeQuery({ requestPolicy: "network-only" }),
        pollInterval
      );
      return () => clearTimeout(id);
    }
  }, [fetching, pollInterval, pause, executeQuery]);

  return { ...result, executor: executeQuery };
};

export const useMutation = <T = any, V = object>(
  query: string | DocumentNode
): UseMutationResponse<T, V> => {
  const [state, op] = urqlUseMutation<T, V>(query);
  const logOperation = (
    variables?: V,
    context?: Partial<OperationContext>
  ): Promise<OperationResult<T>> =>
    op(variables, context).catch(error => {
      datadogLogs.logger.error("Internal Error In Mutation", error);
      throw error;
    });

  return [state, logOperation];
};

export const useSubscription = <T = any, R = T, V = object>(
  args: UseSubscriptionArgs<V>,
  handler?: SubscriptionHandler<T, R> | undefined
): UseSubscriptionResponse<R, V> => {
  const [state, opt] = urqlUseSubscription<T, R, V>(args, handler);
  return [state, opt];
};
